import { Car, Home, HeartPulse, Heart, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/lib/site";

const SEGUROS = [
  {
    id: "auto",
    tipo: "Auto",
    icon: Car,
    titulo: "Seguro de Auto",
    texto:
      "Maneja tranquilo. Te protegemos ante choques, robo y daños a terceros, con asistencia vial y apoyo legal cuando lo necesites.",
    bullets: [
      "Cobertura amplia, limitada o de responsabilidad civil",
      "Asistencia en el lugar del siniestro",
      "Opciones para auto particular, de plataforma o flotillas",
    ],
    boton: "Cotiza tu auto",
    imagen: IMAGES.auto,
    alt: "Familia feliz saliendo de viaje en su coche",
  },
  {
    id: "hogar",
    tipo: "Hogar",
    icon: Home,
    titulo: "Seguro de Hogar",
    texto:
      "Tu casa es tu patrimonio. Protégela contra incendio, robo, fenómenos naturales y daños por agua, ya seas dueño o inquilino.",
    bullets: [
      "Estructura y contenidos",
      "Responsabilidad civil familiar",
      "Asistencia de plomería, cerrajería y electricidad",
    ],
    boton: "Protege tu hogar",
    imagen: IMAGES.hogar,
    alt: "Pareja con niños y perro relajados en la sala de su casa",
  },
  {
    id: "gmm",
    tipo: "Gastos Médicos Mayores",
    icon: HeartPulse,
    titulo: "Gastos Médicos Mayores",
    texto:
      "Tu salud no puede esperar. Accede a los mejores hospitales y médicos sin poner en riesgo tus ahorros.",
    bullets: [
      "Planes individuales, familiares y empresariales",
      "Red hospitalaria amplia en todo el país",
      "Te acompañamos en cada trámite y reembolso",
    ],
    boton: "Cotiza tu GMM",
    imagen: IMAGES.gmm,
    alt: "Mamá abrazando a su bebé sonriente con luz cálida",
  },
  {
    id: "vida",
    tipo: "Vida",
    icon: Heart,
    titulo: "Seguro de Vida",
    texto:
      "El regalo más grande para quienes amas: la tranquilidad de que su futuro está asegurado, pase lo que pase.",
    bullets: [
      "Protección por fallecimiento e invalidez",
      "Planes con ahorro para retiro o la educación de tus hijos",
      "Beneficios fiscales en planes deducibles",
    ],
    boton: "Protege a tu familia",
    imagen: IMAGES.vida,
    alt: "Papá jugando con su hija en el jardín al atardecer",
  },
];

export const Seguros = ({ onCotizar }) => (
  <section id="seguros" data-testid="seguros-section" className="bg-white py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="max-w-2xl">
        <p className="font-display text-xs font-semibold uppercase tracking-[0.25em] text-[#1BA8E0]">
          Seguros
        </p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-[#0C5F8A] sm:text-3xl lg:text-4xl">
          Un seguro para cada etapa de tu vida
        </h2>
        <p className="mt-4 text-base leading-relaxed text-[#2E3338]/75 sm:text-lg">
          Comparamos por ti entre varias aseguradoras para encontrar la cobertura justa al mejor precio.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-8 sm:grid-cols-2 xl:grid-cols-4">
        {SEGUROS.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.1} className="h-full">
            <article
              data-testid={`seguro-card-${s.id}`}
              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#1BA8E0]/15 bg-white shadow-[0_10px_30px_-5px_rgba(12,95,138,0.08)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_24px_50px_-12px_rgba(12,95,138,0.22)]"
            >
              <div className="relative">
                <div className="h-48 overflow-hidden">
                  <img
                    src={s.imagen}
                    alt={s.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0C5F8A]/35 to-transparent" />
                </div>
                <div className="absolute -bottom-6 left-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-lg">
                  <s.icon className="h-6 w-6 text-[#1BA8E0]" strokeWidth={2.2} />
                  <span className="absolute -right-1 -top-1 h-3.5 w-3.5 rounded-full border-2 border-white bg-[#F5B21B]" />
                </div>
              </div>
              <div className="flex flex-1 flex-col px-5 pb-6 pt-10">
                <h3 className="font-display text-lg font-semibold text-[#0C5F8A]">{s.titulo}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-[#2E3338]/75">{s.texto}</p>
                <ul className="mt-4 space-y-2.5">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-[#2E3338]/85">
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#E8F6FD]">
                        <Check className="h-3 w-3 text-[#1BA8E0]" strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => onCotizar(s.tipo)}
                  data-testid={`seguro-cta-${s.id}`}
                  className="mt-6 w-full rounded-full bg-[#F5B21B] py-3 font-display text-sm font-semibold text-[#0C5F8A] transition-all duration-300 hover:bg-[#E0A012] group-hover:shadow-[0_10px_24px_-8px_rgba(245,178,27,0.7)]"
                >
                  {s.boton}
                </button>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
